import { useState, useEffect, useRef } from 'react';
import Modal from '../../../components/Modal';
import StatusBadge from '../../../components/StatusBadge';
import { useApiClient } from '../../../api/client';
import { useToast } from '../../../context/ToastContext';
import { fmtDate } from '../../../utils';
import type { ChangeRequest, StakeholderResponse } from '../../../types';

interface Props { record: ChangeRequest; cid: string; onRefresh: () => Promise<void>; }

export default function ResponsesSection({ record, cid, onRefresh }: Props) {
  const api = useApiClient();
  const { showToast } = useToast();
  const [rows, setRows]       = useState<StakeholderResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewRow, setViewRow] = useState<StakeholderResponse | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const advancedRef = useRef(false);

  async function loadRows() {
    try {
      const data = await api.get<StakeholderResponse[]>(`/changes/${encodeURIComponent(cid)}/responses`);
      setRows(data);
      const allResolved = data.length > 0 && data.every(r => r.responseStatus !== 'Pending');
      if (allResolved) {
        if (timerRef.current) clearInterval(timerRef.current);
        // Server auto-advanced to Planning — refresh parent to switch section
        if (!advancedRef.current) {
          advancedRef.current = true;
          await onRefresh();
        }
      }
    } catch (err) {
      showToast((err as Error).message, 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRows();
    timerRef.current = setInterval(loadRows, 60000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [cid]);

  const responded  = rows.filter(r => r.responseStatus === 'Responded').length;
  const pending    = rows.filter(r => r.responseStatus === 'Pending').length;
  const noResponse = rows.filter(r => r.responseStatus === 'No Response').length;
  const allResolved = rows.length > 0 && rows.every(r => r.responseStatus !== 'Pending');

  return (
    <>
      <div className="section-panel">
        <div className="section-panel__header">
          <h2 className="section-panel__title"><i className="fa-solid fa-inbox" aria-hidden="true" /> Step 3 — Stakeholder Responses</h2>
          <div style={{ display: 'flex', gap: 8 }}>
            <span className="badge badge--accent">Responded: {responded}</span>
            <span className="badge badge--warn">Pending: {pending}</span>
            <span className="badge badge--muted">No Response: {noResponse}</span>
          </div>
        </div>
        <div className="section-panel__body">
          {/* Header stats */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 12, marginBottom: 20 }}>
            {[
              { label: 'Responded',   value: responded,  color: 'var(--accent)' },
              { label: 'Pending',     value: pending,    color: 'var(--warn)' },
              { label: 'No Response', value: noResponse, color: 'var(--ink-dim)' },
              { label: 'Deadline',    value: fmtDate(record.notificationDeadline), color: 'var(--foreground)', isText: true },
            ].map(s => (
              <div key={s.label} style={{ padding: '14px 16px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--border)', background: 'var(--surface-muted)' }}>
                <div style={{ fontSize: '0.75rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--ink-dim)' }}>{s.label}</div>
                <div style={{ fontSize: s.isText ? '1rem' : '1.5rem', fontWeight: 800, marginTop: 4, color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          <div className="table-wrapper">
            <table className="table" aria-label="Stakeholder responses">
              <thead>
                <tr>
                  <th>Name</th><th>Department</th><th>Status</th><th>Affected</th><th>Date Responded</th><th></th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6}><div className="loading-state" style={{ padding: '32px' }}><div className="spinner" /></div></td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={6} className="table-empty">No responses yet.</td></tr>}
                {rows.map(r => (
                  <tr key={r.id}>
                    <td data-label="Name">{r.teamLeadName}</td>
                    <td data-label="Department">{r.department}</td>
                    <td data-label="Status"><StatusBadge status={r.responseStatus} variant="response" /></td>
                    <td data-label="Affected">{r.isAffected ? <span className={`badge ${r.isAffected === 'Yes' ? 'badge--warn' : 'badge--muted'}`}>{r.isAffected}</span> : '—'}</td>
                    <td data-label="Date Responded">{fmtDate(r.dateResponded)}</td>
                    <td data-label="" className="action-cell"><button className="btn btn--secondary btn--sm" onClick={() => setViewRow(r)}><i className="fa-solid fa-eye" aria-hidden="true" /> View</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {allResolved && (
            <div style={{ marginTop: 20, display: 'flex', alignItems: 'center', gap: 10, color: 'var(--ink-dim)', fontSize: '0.875rem' }}>
              <div className="spinner spinner--sm" />
              All responses received — advancing to planning…
            </div>
          )}
          {!allResolved && (
            <p className="form-hint" style={{ marginTop: 16 }}>
              <i className="fa-solid fa-circle-info" aria-hidden="true" /> Planning will begin automatically once all stakeholders have responded.
            </p>
          )}
        </div>
      </div>

      {/* Response detail modal */}
      <Modal open={!!viewRow} onClose={() => setViewRow(null)} title="Stakeholder Response" size="lg"
        footer={<button className="btn btn--secondary" onClick={() => setViewRow(null)}>Close</button>}
      >
        {viewRow && (
          <>
            <div className="field-display-grid">
              <div className="field-display"><div className="field-display__label">Name</div><div className="field-display__value">{viewRow.teamLeadName}</div></div>
              <div className="field-display"><div className="field-display__label">Department</div><div className="field-display__value">{viewRow.department}</div></div>
              <div className="field-display"><div className="field-display__label">Status</div><div className="field-display__value"><StatusBadge status={viewRow.responseStatus} variant="response" /></div></div>
              <div className="field-display"><div className="field-display__label">Affected</div><div className="field-display__value">{viewRow.isAffected ?? '—'}</div></div>
            </div>
            {viewRow.isAffected === 'Yes' && (
              <>
                <div className="divider" />
                <div className="field-display-grid">
                  <div className="field-display"><div className="field-display__label">How Affected</div><div className="field-display__value">{viewRow.howAffected ?? '—'}</div></div>
                  <div className="field-display"><div className="field-display__label">Team Role</div><div className="field-display__value">{viewRow.teamRole ?? '—'}</div></div>
                  <div className="field-display"><div className="field-display__label">Support Needs</div><div className="field-display__value">{viewRow.supportNeeds ?? '—'}</div></div>
                  <div className="field-display"><div className="field-display__label">Concerns</div><div className="field-display__value">{viewRow.concerns ?? '—'}</div></div>
                  <div className="field-display"><div className="field-display__label">Effort Estimate</div><div className="field-display__value">{viewRow.effortEstimate ?? '—'}</div></div>
                </div>
              </>
            )}
          </>
        )}
      </Modal>
    </>
  );
}
